import { Network, Bot, Box, Globe, KeyRound, Laptop, NotebookPen, GitBranch, Cpu, FileText, Image, ShieldCheck, Terminal } from 'lucide-react';

// Shared API-reference data for /docs (the PlatformDocs list) and the per-endpoint
// deep-link pages at /docs/{slug} (EndpointDoc). Every endpoint below is backed by
// a live route in the Go control plane (server/main.go, server/agents.go,
// server/sites.go) or by the OpenAI-compatible model gateway. Keep this in sync
// with those handlers — and with the slug mirror in server/sitemap.go
// (sitemapDocEndpoints) that lists each page in /sitemap.xml.

export type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface Endpoint {
  method: Method;
  path: string;
  summary: string;
  auth?: 'key' | 'session' | 'none';
  // noDoc marks URL-pattern rows (public file serving) that are not callable API
  // endpoints, so they stay plain rows with no /docs/{slug} page or sitemap entry.
  noDoc?: boolean;
}

export interface Param {
  name: string;
  type: string;
  required?: boolean;
  desc: string;
}

export interface DocSection {
  id: string;
  label: string;
  icon: typeof Network;
  title: string;
  intro: string;
  baseUrl?: string;
  endpoints?: Endpoint[];
  params?: { heading: string; rows: Param[] };
  example?: { label: string; code: string };
}

export const sections: DocSection[] = [
  {
    id: 'auth',
    label: 'Auth & keys',
    icon: KeyRound,
    title: 'Authentication',
    intro:
      'Use account API keys for unattended servers and OAuth for people. `app login` is a native public-client flow with PKCE; its api-scoped token works across the CLI, REST API, desktop cloud bridge, and MCP. To authenticate users in your own app, register a client at /developers. Browser, hosted subdomain, and mobile clients are secretless and require PKCE S256; server clients exchange with a one-time client secret. The hosted appnz-auth.js client provides Firebase-style sign-in, current-user, token, and auth-state helpers.',
    baseUrl: 'https://app.nz',
    endpoints: [
      { method: 'GET', path: '/api/keys', summary: 'List keys (metadata only, no secrets)', auth: 'key' },
      { method: 'POST', path: '/api/keys', summary: 'Create a key; response includes the one-time secret', auth: 'key' },
      { method: 'POST', path: '/api/keys/rotate', summary: 'Rotate by id, by name, or all at once', auth: 'key' },
      { method: 'DELETE', path: '/api/keys?id=key_123', summary: 'Revoke a key', auth: 'key' },
      { method: 'GET', path: '/api/me', summary: 'Current account for the supplied key or session', auth: 'key' },
      { method: 'GET', path: '/authorize', summary: 'Start Sign in with app.nz (Authorization Code + PKCE)', auth: 'none', noDoc: true },
      { method: 'POST', path: '/token', summary: 'Exchange a one-use code for a scoped access token', auth: 'none', noDoc: true },
      { method: 'GET', path: '/userinfo', summary: 'Load the profile granted to an OAuth access token', auth: 'key', noDoc: true },
      { method: 'POST', path: '/introspect', summary: 'Server apps validate their app-scoped access tokens or API keys', auth: 'none', noDoc: true },
      { method: 'POST', path: '/revoke', summary: 'Revoke an OAuth token on sign-out', auth: 'none', noDoc: true },
      { method: 'GET', path: '/api/oauth/keys', summary: 'List API keys owned by the authorized app (keys:manage)', auth: 'key', noDoc: true },
      { method: 'GET', path: '/.well-known/oauth-authorization-server', summary: 'OAuth provider discovery metadata', auth: 'none', noDoc: true },
    ],
    example: {
      label: 'Create a key and call the API',
      code: `# Create a key (secret returned once)
curl -sX POST https://app.nz/api/keys \\
  -H "Authorization: Bearer $APP_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"deploy bot"}'

# Use it on every other request
curl -s https://app.nz/api/me \\
  -H "Authorization: Bearer pk_live_..."`,
    },
  },
  {
    id: 'developer-apps',
    label: 'Developer apps',
    icon: ShieldCheck,
    title: 'Sign in with app.nz',
    intro:
      'Give your product app.nz accounts without building identity infrastructure. Register the app and its exact callback URLs in the Developer Apps console, choose the smallest set of scopes it needs, then use Authorization Code with PKCE. Browser, hosted-site, mobile, desktop, and CLI clients are public clients and never receive a secret; confidential server apps exchange the code on their backend.',
    baseUrl: 'https://app.nz',
    endpoints: [
      { method: 'GET', path: '/authorize', summary: 'Start authorization with response_type=code and a PKCE S256 challenge', auth: 'none', noDoc: true },
      { method: 'POST', path: '/token', summary: 'Exchange a one-use authorization code and PKCE verifier', auth: 'none', noDoc: true },
      { method: 'GET', path: '/userinfo', summary: 'Read the profile granted to the current access token', auth: 'key', noDoc: true },
      { method: 'POST', path: '/introspect', summary: 'Let a confidential app validate an app-scoped token', auth: 'none', noDoc: true },
      { method: 'POST', path: '/revoke', summary: 'Revoke the current access token during sign-out', auth: 'none', noDoc: true },
      { method: 'GET', path: '/.well-known/oauth-authorization-server', summary: 'OAuth authorization-server discovery metadata', auth: 'none', noDoc: true },
    ],
    params: {
      heading: 'Setup checklist',
      rows: [
        { name: 'Redirect URL', type: 'exact URL', required: true, desc: 'Register every production and local callback exactly. Hosted sites normally use https://<slug>.app.nz/auth/callback.' },
        { name: 'profile', type: 'scope', required: true, desc: 'Stable account id, email, and handle for the signed-in person.' },
        { name: 'api', type: 'scope', desc: 'Call app.nz REST APIs and authenticated MCP tools as that person.' },
        { name: 'credits:read', type: 'scope', desc: 'Read the person’s credit balance and this app’s ledger entries.' },
        { name: 'credits:spend', type: 'scope', desc: 'Charge the person’s app.nz balance for work your app performs.' },
        { name: 'models:chat', type: 'scope', desc: 'Run chat completions at POST /v1/chat/completions on the model(s) listed in the authorize request’s models parameter (e.g. models=gpt-5.5), billed to the person’s credits. Pinned: any other model, fallback, or endpoint is refused.' },
        { name: 'keys:manage', type: 'server scope', desc: 'Create and revoke API keys owned by the app; confidential server apps only.' },
      ],
    },
    example: {
      label: 'Browser and hosted-site quickstart',
      code: `import { createAuthClient } from 'https://app.nz/appnz-auth.js';

const auth = createAuthClient({
  clientId: 'YOUR_CLIENT_ID',
  redirectUri: window.location.origin + '/auth/callback',
  scopes: ['profile', 'api'],
});

// Sign-in button
await auth.signIn({ returnTo: '/dashboard' });

// On /auth/callback
const result = await auth.handleRedirect();
if (result) window.location.replace(result.returnTo);

// Restore the user and call app.nz with the saved access token
const user = await auth.getUser();
const response = await auth.fetch('/api/me');`,
    },
  },
  {
    id: 'cli',
    label: 'CLI',
    icon: Terminal,
    title: 'The app CLI',
    intro:
      'Every endpoint in these docs is scriptable from one small binary. The CLI shares keys with the API and web app, streams chat, deploys apps and sites, drives coding agents, and mirrors the gh command layout for repos, issues, PRs, and releases. Any command takes --json; anything without a dedicated command is reachable via app api. The full command reference lives at /cli.',
    params: {
      heading: 'Core commands',
      rows: [
        { name: 'app login', type: 'auth', desc: 'Open app.nz in the browser, authorize with PKCE, and save the scoped login; app whoami verifies.' },
        { name: 'app apps deploy [dir]', type: 'hosting', desc: 'Package + build + release an appnz.yaml app (static or server runtime) at <name>.app.nz.' },
        { name: 'app apps logs <slug>', type: 'hosting', desc: 'Build and runtime logs for the latest (or a given) deploy.' },
        { name: 'app apps marketplace [query]', type: 'hosting', desc: 'Browse apps and games built on or showcased by app.nz.' },
        { name: 'app apps publish [slug] --url <url>', type: 'hosting', desc: 'Instantly enrich a hosted app card or publish an external community app with automatic OG metadata discovery.' },
        { name: 'app sites deploy <slug> <dir>', type: 'hosting', desc: 'Mirror a directory of static files to a hosted site.' },
        { name: 'app agent run --prompt "..." --repo o/n', type: 'agents', desc: 'Launch a cloud coding agent; get/list/cancel manage tasks.' },
        { name: 'app chat "..." [--web] [--deep]', type: 'models', desc: 'Streamed chat through the gateway; --model pins a route.' },
        { name: 'app api <METHOD> <path> [json]', type: 'escape hatch', desc: 'Call any documented endpoint directly.' },
      ],
    },
    example: {
      label: 'Install, build with an agent, deploy a game',
      code: `curl -fsSL https://app.nz/cli.sh | sh
app login

# hand the build to a cloud agent
app agent run --prompt "build a neon snake HTML5 game" --repo you/neon-snake

# pull the agent's files, then ship to https://neon-snake.app.nz
app agent apply <task-id> --dir ./neon-snake
app apps deploy ./neon-snake`,
    },
  },
  {
    id: 'models',
    label: 'Models gateway',
    icon: Network,
    title: 'Models gateway',
    intro:
      'An OpenAI- and Anthropic-compatible API in front of 15+ providers. OpenAI clients use https://app.nz/v1; Claude Code and Anthropic Agent SDK use the root https://app.nz because they append /v1/messages. Use app/auto to select a model and thinking depth from the prompt, or pin a provider model. Stable long Anthropic prefixes are cached automatically while explicit cache controls are preserved.',
    baseUrl: 'https://app.nz/v1',
    endpoints: [
      { method: 'POST', path: '/v1/chat/completions', summary: 'Chat completions (streaming + tools)', auth: 'key' },
      { method: 'POST', path: '/v1/messages', summary: 'Anthropic-compatible messages (supports thinking)', auth: 'key' },
      { method: 'GET', path: '/v1/models', summary: 'List routable models and aliases', auth: 'key' },
      { method: 'POST', path: '/v1/embeddings', summary: 'Text embeddings', auth: 'key' },
      { method: 'POST', path: '/v1/images/generations', summary: 'Image generation', auth: 'key' },
      { method: 'POST', path: '/v1/videos/generations', summary: 'Video generation', auth: 'key' },
      { method: 'POST', path: '/v1/videos/edits', summary: 'Video editing', auth: 'key' },
      { method: 'POST', path: '/v1/videos/extensions', summary: 'Video extension', auth: 'key' },
      { method: 'GET', path: '/v1/videos/{request_id}', summary: 'Poll xAI video jobs', auth: 'key' },
      { method: 'POST', path: '/v1/music/generations', summary: 'Music generation', auth: 'key' },
      { method: 'POST', path: '/v1/audio/generations', summary: 'Audio and SFX generation', auth: 'key' },
      { method: 'POST', path: '/v1/3d/generations', summary: 'Image-to-3D GLB generation', auth: 'key' },
      { method: 'POST', path: '/v1/panoramas/generations', summary: 'Image-to-360 panorama or spatial-world generation', auth: 'key' },
      { method: 'POST', path: '/v1/audio/speech', summary: 'Text-to-speech', auth: 'key' },
      { method: 'POST', path: '/v1/audio/transcriptions', summary: 'Audio transcription', auth: 'key' },
      { method: 'POST', path: '/v1/search', summary: 'Web and papers search', auth: 'key' },
      { method: 'POST', path: '/api/audio/generate', summary: 'Generate music or SFX and save to the indexed audio library', auth: 'key' },
      { method: 'GET', path: '/api/audio/search?q=rain', summary: 'Semantic + keyword search over generated public/own audio', auth: 'none' },
      { method: 'GET', path: '/api/audio', summary: 'List recent public/own generated audio', auth: 'none' },
    ],
    params: {
      heading: 'Model routes (the model field)',
      rows: [
        { name: 'app/auto', type: 'route', desc: 'Default. Reads the prompt and picks the backend.' },
        { name: 'app/auto-code', type: 'route', desc: 'Bias toward strong coding models.' },
        { name: 'app/auto-fast', type: 'route', desc: 'Lowest latency for interactive use.' },
        { name: 'app/auto-cheap', type: 'route', desc: 'Bias toward lowest cost per token.' },
        { name: 'app/auto-reasoning', type: 'route', desc: 'Route reasoning depth automatically.' },
        { name: 'app/auto-vision', type: 'route', desc: 'Bias toward image-input models.' },
        { name: 'app/auto-image', type: 'route', desc: 'Bias toward image-generation models.' },
        { name: 'routing_strategy', type: 'param', desc: 'price by default; use config to preserve catalogue order or app/auto-fast for latency bias.' },
        { name: 'provider/model', type: 'pin', desc: 'Pin a specific upstream model, e.g. anthropic/claude or openai/gpt.' },
      ],
    },
    example: {
      label: 'Chat completion with auto routing',
      code: `curl -s https://app.nz/v1/chat/completions \\
  -H "Authorization: Bearer pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "app/auto",
    "messages": [{"role": "user", "content": "Explain CRDTs in one paragraph."}],
    "reasoning_effort": "auto",
    "stream": false
  }'`,
    },
  },
  {
    id: 'textures',
    label: 'Texture agent',
    icon: Image,
    title: 'Verified seamless texture API',
    intro:
      'Generate repeatable materials through eBank.nz, local Z-Image, or FAL, then pass every candidate through app.nz’s wrap-boundary scorer. The agent route adds a free exact-periodic control, varies seeds, builds real 2×2 proofs, applies one bounded edge repair when needed, rejects remaining failures, and selects the strongest accepted tile. One app credit is $0.001. At 512×512, eBank candidates cost up to 48 credits each, background Z-Image costs 9, the procedural control is free, and FAL balanced tiling is estimated at 22; the response states the maximum before work starts.',
    baseUrl: 'https://app.nz',
    endpoints: [
      { method: 'GET', path: '/api/textures', summary: 'Search accepted tiles and 2×2 proofs', auth: 'none' },
      { method: 'POST', path: '/api/textures/jobs', summary: 'Queue one generated-and-verified tile', auth: 'key' },
      { method: 'GET', path: '/api/textures/{id}', summary: 'Poll one texture and inspect seam metrics', auth: 'key' },
      { method: 'POST', path: '/api/textures/agent', summary: 'Run a bounded candidate search, verification, repair, and ranking pass', auth: 'key' },
      { method: 'GET', path: '/api/textures/agent/{id}', summary: 'Poll the experiment and retrieve its winning proof', auth: 'key' },
    ],
    params: {
      heading: 'Agent controls and 512×512 prices',
      rows: [
        { name: 'prompt', type: 'string', required: true, desc: 'Material or pattern brief, up to 600 characters.' },
        { name: 'provider: ebank', type: '48 credits/candidate', desc: 'RA1 Seamless Texture LoRA through eBank.nz. Default agent provider.' },
        { name: 'provider: zimage', type: '9 credits/candidate', desc: 'Local background-priority generation; one active candidate per user.' },
        { name: 'provider: seamless-tiling', type: '~22 credits/candidate', desc: 'FAL Ideogram tiling at balanced 512×512 pricing.' },
        { name: 'variants', type: 'int 1-4', desc: 'Paid candidate count. Defaults to 3; the procedural control does not count.' },
        { name: 'includeProcedural', type: 'boolean', desc: 'Include a free periodic-by-construction control. Defaults to true.' },
      ],
    },
    example: {
      label: 'Run and poll the texture agent',
      code: `# Three eBank candidates + one free procedural control: up to 144 credits ($0.144).
experiment=$(curl -sX POST https://app.nz/api/textures/agent \\
  -H "Authorization: Bearer $APP_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"prompt":"weathered slate with fine moss, neutral overhead light","provider":"ebank","variants":3,"seed":4200}')

id=$(printf '%s' "$experiment" | jq -r '.experiment.id')
curl -s "https://app.nz/api/textures/agent/$id" \\
  -H "Authorization: Bearer $APP_API_KEY" | jq '.experiment | {status, winner, results}'

# Same workflow through the CLI or MCP:
app texture run --prompt "weathered slate with fine moss" --provider ebank --variants 3
# MCP tools: run_texture_agent, get_texture_agent`,
    },
  },
  {
    id: 'comfy',
    label: 'ComfyUI workflows',
    icon: Image,
    title: 'ComfyUI workflow API',
    intro:
      'Discover and download tested API-format graphs, deploy one as a scale-to-zero GPU endpoint, and poll hosted output artifacts. app.nz API Input and API Output nodes make typed names part of the graph itself; deployment workflows and their contracts persist in the platform database and can be exported exactly. Serverless workers use pinned reviewed node packs and never install arbitrary plugin code at runtime. The public catalogue includes a ten-part foundations course plus twelve multimedia Creative Systems Labs.',
    baseUrl: 'https://app.nz',
    endpoints: [
      { method: 'GET', path: '/api/comfy/tutorials', summary: 'List the 10-part course with runnable workflow links', auth: 'none' },
      { method: 'GET', path: '/api/comfy/labs', summary: 'List 12 multimedia labs with runnable workflow links', auth: 'none' },
      { method: 'GET', path: '/api/comfy/plugins', summary: 'List pinned serverless and recommended editable-pod node packs', auth: 'none' },
      { method: 'GET', path: '/api/comfy/spaces', summary: 'List vetted workflows, inputs, provenance, VRAM, and verification', auth: 'none' },
      { method: 'GET', path: '/api/comfy/spaces/{slug}', summary: 'Get one API-format graph and its complete contract', auth: 'none' },
      { method: 'GET', path: '/api/comfy/spaces/{slug}/download', summary: 'Download exact runnable workflow JSON', auth: 'none' },
      { method: 'POST', path: '/api/comfy/spaces/{slug}/deploy', summary: 'Deploy a gallery workflow', auth: 'key' },
      { method: 'GET', path: '/api/comfy/models?q=proteus', summary: 'Search model sources and global mirror state', auth: 'none' },
      { method: 'POST', path: '/api/comfy/{deployment_id}/run', summary: 'Queue named workflow inputs', auth: 'key' },
      { method: 'GET', path: '/api/comfy/{deployment_id}/download', summary: 'Export the exact persisted deployment workflow', auth: 'key' },
      { method: 'GET', path: '/api/comfy/runs/{run_id}', summary: 'Poll timing, cost, errors, and output artifacts', auth: 'key' },
    ],
    params: {
      heading: 'Gallery deploy and run fields',
      rows: [
        { name: 'name', type: 'string', desc: 'Optional deployment name; defaults to the workflow slug.' },
        { name: 'hardware', type: 'string', desc: 'GPU machine type or auto.' },
        { name: 'mode', type: 'string', desc: 'auto | serverless | pod | local | dedicated.' },
        { name: 'inputs', type: 'object', desc: 'Named values from the selected workflow apiInputs contract.' },
        { name: 'apiOutputs', type: 'array', desc: 'Named output node contract; matching files include the same name.' },
        { name: 'image/video/audio', type: 'string', desc: 'Public URL or artifact:<id> for mapped media inputs.' },
      ],
    },
    example: {
      label: 'Run image, audio-reactive, or talking-character graphs',
      code: `# Proteus V0.4: deploy the tested seven-node SDXL workflow
curl -sX POST https://app.nz/api/comfy/spaces/proteus-v04-text-to-image/deploy \\
  -H "Authorization: Bearer $APP_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"mode":"auto","name":"proteus-course"}'

# Queue named inputs using the deployment id returned above
curl -sX POST https://app.nz/api/comfy/DEPLOYMENT_ID/run \\
  -H "Authorization: Bearer $APP_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"inputs":{"prompt":"ceramic observatory under an aurora","seed":42,"steps":24,"cfg":5}}'

# CuteDSL-accelerated Z-Image Turbo uses the same contract
curl -sX POST https://app.nz/api/comfy/spaces/cute-zimage-turbo/deploy \\
  -H "Authorization: Bearer $APP_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"mode":"auto"}'

# CPU audio-reactive frames: map a worker audio file to spectrum animation
curl -sX POST https://app.nz/api/comfy/spaces/audio-reactive-visualizer/deploy \\
  -H "Authorization: Bearer $APP_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"mode":"auto"}'

# Inspect the talking-character graph and its open-source dependencies first
curl -s https://app.nz/api/comfy/spaces/liveavatar-talking-character | jq '.space.apiInputs, .space.openSource'`,
    },
  },
  {
    id: 'agents',
    label: 'Agents',
    icon: Bot,
    title: 'Agents API',
    intro:
      'Run cloud coding agents against a repo and a prompt, then poll status, stream step events, inspect changed files, and cancel or retry. GET /api/agents/config returns the option lists (engines, models, reasoning efforts, machine types, providers, skills) and needs no auth so a UI can render the form before sign-in.',
    baseUrl: 'https://app.nz',
    endpoints: [
      { method: 'GET', path: '/api/agents/config', summary: 'Option lists for the agent form', auth: 'none' },
      { method: 'POST', path: '/api/agents/tasks', summary: 'Launch an agent task', auth: 'key' },
      { method: 'GET', path: '/api/agents/tasks', summary: 'List your agent tasks', auth: 'key' },
      { method: 'GET', path: '/api/agents/tasks/{id}', summary: 'Get a task with its current status', auth: 'key' },
      { method: 'GET', path: '/api/agents/tasks/{id}/events', summary: 'Step-by-step events and status', auth: 'key' },
      { method: 'POST', path: '/api/agents/tasks/{id}/cancel', summary: 'Cancel a running task', auth: 'key' },
      { method: 'POST', path: '/api/agents/tasks/{id}/retry', summary: 'Retry a finished or failed task', auth: 'key' },
      { method: 'GET', path: '/api/agents/tasks/{id}/files', summary: 'List files the agent changed', auth: 'key' },
      { method: 'PUT', path: '/api/agents/tasks/{id}/files/{fileId}', summary: 'Edit a changed file before review', auth: 'key' },
    ],
    params: {
      heading: 'POST /api/agents/tasks body',
      rows: [
        { name: 'prompt', type: 'string', required: true, desc: 'What the agent should do (max 20k chars).' },
        { name: 'source', type: 'string', desc: 'Provider that runs it: openpaths (default, our cloud) | devin | cursor | codex-cloud. See the Agents SDK section.' },
        { name: 'repo', type: 'string', desc: 'Target repo as owner/name.' },
        { name: 'branch', type: 'string', desc: 'Working branch to create or use.' },
        { name: 'baseBranch', type: 'string', desc: 'Branch to base the work on.' },
        { name: 'model', type: 'string', desc: 'Model route, e.g. app/auto-code.' },
        { name: 'engine', type: 'string', desc: 'Agent engine (see /config).' },
        { name: 'reasoningEffort', type: 'string', desc: 'none | low | medium | high | xhigh | auto.' },
        { name: 'machineType', type: 'string', desc: 'Worker capability tier (see /config), e.g. auto, cpx32, gpu-a100, or win-cpx32 for a Windows worker.' },
        { name: 'provider', type: 'string', desc: 'Execution tier, e.g. shared (auto fleet), cloud CPU, or cloud GPU (see /config).' },
        { name: 'skills', type: 'string[]', desc: 'Enabled skills, e.g. ["github","visualbench"].' },
        { name: 'autoMergePr', type: 'boolean', desc: 'Auto-merge the PR when checks pass.' },
        { name: 'autoNextSteps', type: 'boolean', desc: 'Let the agent queue follow-up steps.' },
        { name: 'spendCapUsd', type: 'number', desc: 'Hard spend ceiling for the task.' },
        { name: 'timeoutSeconds', type: 'number', desc: 'Wall-clock timeout.' },
        { name: 'projectId', type: 'string', desc: 'Project to bill and scope to; defaults to your default project.' },
        { name: 'title', type: 'string', desc: 'Display title; derived from the prompt if omitted.' },
      ],
    },
    example: {
      label: 'Launch an agent',
      code: `curl -sX POST https://app.nz/api/agents/tasks \\
  -H "Authorization: Bearer pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "prompt": "add usage analytics to the dashboard",
    "repo": "acme/ai-dashboard",
    "model": "app/auto-code",
    "reasoningEffort": "high",
    "machineType": "auto",
    "provider": "shared",
    "skills": ["github", "visualbench"],
    "spendCapUsd": 2.00,
    "autoMergePr": false
  }'`,
    },
  },
  {
    id: 'agents-sdk',
    label: 'Agents SDK',
    icon: Bot,
    title: 'Agents SDK — one API, every provider',
    intro:
      'The same /api/agents surface is a meta-agent router: set `source` to choose who runs the task. openpaths (the default) runs it on app.nz workers using our model credentials — which take precedence over any provider key when running in our cloud — and opens PRs through our GitHub app. devin, cursor, and codex-cloud launch the run on that vendor and the result (status, logs, diff, pull request) normalizes back into the same task — so every provider reads through one trace. GET /api/agents/config returns `sources` plus a `sourcesConfigured` map so a UI can disable providers that lack server credentials.',
    baseUrl: 'https://app.nz',
    endpoints: [
      { method: 'GET', path: '/api/agents/config', summary: 'Sources + which are configured, plus models/engines', auth: 'none' },
      { method: 'POST', path: '/api/agents/tasks', summary: 'Launch on any provider via the source field', auth: 'key' },
      { method: 'GET', path: '/api/agents/tasks', summary: 'List your runs across all providers', auth: 'key' },
      { method: 'GET', path: '/api/agents/tasks/{id}', summary: 'Normalized trace: steps, messages, diff, PR', auth: 'key' },
      { method: 'POST', path: '/api/agents/tasks/{id}/cancel', summary: 'Cancel a running task', auth: 'key' },
    ],
    params: {
      heading: 'Source values',
      rows: [
        { name: 'openpaths', type: 'source', desc: 'Default. Runs on app.nz workers with our credentials; engine selects Claude Code / Codex / Gemini. Real PRs via our GitHub app.' },
        { name: 'devin', type: 'source', desc: 'Launches a Devin cloud session. Needs server DEVIN_API_KEY + DEVIN_ORG_ID.' },
        { name: 'cursor', type: 'source', desc: 'Launches a Cursor cloud agent with auto-PR. Needs server CURSOR_API_KEY.' },
        { name: 'codex-cloud', type: 'source', desc: 'Launches an OpenAI Codex Cloud task against a GitHub-connected repo. Needs CODEX_CLOUD_ENV_ID + the codex CLI.' },
      ],
    },
    example: {
      label: 'Launch on Devin (or swap source for cursor / codex-cloud / openpaths)',
      code: `curl -sX POST https://app.nz/api/agents/tasks \\
  -H "Authorization: Bearer pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "source": "devin",
    "prompt": "fix failing CI and open a pull request",
    "repo": "lee101/edukids",
    "baseBranch": "main",
    "autoMergePr": false
  }'

# CLI equivalent:
#   app agents-sdk run "fix failing CI and open a PR" --source devin --repo lee101/edukids
#   app agents-sdk providers      # see which providers are configured
#   app agents-sdk logs <id>      # read the normalized trace`,
    },
  },
  {
    id: 'local-workers',
    label: 'Local workers',
    icon: Laptop,
    title: 'Local workers — run agent tasks on your own machines',
    intro:
      'Run `app worker` on any computer after `app login` and it becomes a private worker for your account: it polls app.nz, leases agent tasks created with provider "local", executes them with local engines (our codex fork with automatic fallback to mainline codex, claude, cursor-agent, gemini, grok), and reports steps/diffs/PRs into the normal task trace. Leasing is user-scoped: your workers authenticate every /api/worker call with your API key and only ever see jobs whose user matches your account; fleet workers use the shared X-Worker-Token and never lease provider=local jobs. Job payloads and logs pass through secret redaction, and only allowlisted engines execute. By default a local worker takes only your provider=local jobs; --any-provider opts it into all of your jobs.',
    baseUrl: 'https://app.nz',
    endpoints: [
      { method: 'GET', path: '/api/agents/workers', summary: 'List your registered machines with online state and activeJobs', auth: 'session', noDoc: true },
      { method: 'POST', path: '/api/worker/register', summary: 'Worker announces itself (Bearer API key for user workers; X-Worker-Token for the fleet)', auth: 'key', noDoc: true },
      { method: 'POST', path: '/api/worker/heartbeat', summary: 'Liveness + active job count; drives the online indicator', auth: 'key', noDoc: true },
      { method: 'POST', path: '/api/worker/lease', summary: 'Atomically claim the oldest runnable job you own', auth: 'key', noDoc: true },
      { method: 'POST', path: '/api/worker/complete', summary: 'Report a leased job result', auth: 'key', noDoc: true },
      { method: 'POST', path: '/api/worker/job-creds', summary: 'Short-lived credentials for a leased job', auth: 'key', noDoc: true },
    ],
    params: {
      heading: 'app worker flags',
      rows: [
        { name: '--name <name>', type: 'string', desc: 'Machine display name; defaults to the hostname.' },
        { name: '--dir <path>', type: 'string', desc: 'Working directory for repo checkouts.' },
        { name: '--engines <list>', type: 'string', desc: 'Comma-separated engine allowlist, e.g. codex,claude. Defaults to every installed engine.' },
        { name: '--any-provider', type: 'boolean', desc: 'Also lease your non-local (cloud-queued) jobs, not just provider=local.' },
        { name: '--poll <dur>', type: 'duration', desc: 'Poll interval for the lease loop.' },
        { name: '--lease <n>', type: 'integer', desc: 'Max jobs to run concurrently.' },
        { name: '--once', type: 'boolean', desc: 'Lease at most one job, run it, exit (cron-friendly).' },
        { name: '--dry-run', type: 'boolean', desc: 'Show what would be leased and run without executing.' },
        { name: 'app worker status', type: 'command', desc: 'List your machines, online state, and active jobs from the CLI.' },
      ],
    },
    example: {
      label: 'Turn a desktop into a worker, then fire a task at it from anywhere',
      code: `# on the machine that will do the coding
app login
app worker --name desktop --dir ~/agent-work --engines codex,claude

# from any device (or the /agent composer with provider "Your machine")
app agent run --prompt "add a /health endpoint with a test" \\
  --repo you/api --provider local --model app/auto-code

# see your machines (same data as the studio chips)
app worker status
curl -s https://app.nz/api/agents/workers -H "Authorization: Bearer pk_live_..."`,
    },
  },
  {
    id: 'artifacts',
    label: 'Artifacts',
    icon: FileText,
    title: 'Artifacts API',
    intro:
      'Create, update, list, share, and delete user artifacts. Supported kinds are drawing, doc, sheet, pdf, image, and file. Documents store Markdown text and open in /write; sheets store JSON grid data and open in /sheets, so agents can produce editable office documents directly.',
    baseUrl: 'https://app.nz',
    endpoints: [
      { method: 'GET', path: '/api/artifacts', summary: 'List artifacts visible to you; optional ?kind=doc or ?kind=sheet', auth: 'key' },
      { method: 'POST', path: '/api/artifacts', summary: 'Create an artifact', auth: 'key' },
      { method: 'GET', path: '/api/artifacts/{id}', summary: 'Fetch one visible artifact', auth: 'key' },
      { method: 'PUT', path: '/api/artifacts/{id}', summary: 'Update one of your artifacts', auth: 'key' },
      { method: 'DELETE', path: '/api/artifacts/{id}', summary: 'Delete one of your artifacts', auth: 'key' },
      { method: 'POST', path: '/api/artifacts/{id}/share', summary: 'Create or rotate a public share token', auth: 'key' },
      { method: 'DELETE', path: '/api/artifacts/{id}/share', summary: 'Remove the public share token', auth: 'key' },
      { method: 'GET', path: '/api/artifacts/shared/{token}', summary: 'Fetch a shared artifact by token', auth: 'none' },
    ],
    params: {
      heading: 'Artifact body',
      rows: [
        { name: 'kind', type: 'string', required: true, desc: 'drawing | doc | sheet | pdf | image | file.' },
        { name: 'title', type: 'string', required: true, desc: 'Display title.' },
        { name: 'mime', type: 'string', desc: 'MIME type, e.g. text/markdown for doc or application/json for sheet.' },
        { name: 'data', type: 'string', desc: 'Inline data. Documents use Markdown; sheets use JSON grid data.' },
        { name: 'url', type: 'string', desc: 'Optional https:// or app-relative URL for binary artifacts.' },
        { name: 'teamId', type: 'string', desc: 'Optional team visibility scope.' },
      ],
    },
    example: {
      label: 'Create editable document and sheet artifacts',
      code: `curl -sX POST https://app.nz/api/artifacts \\
  -H "Authorization: Bearer pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"kind":"doc","title":"Runbook","mime":"text/markdown","data":"# Runbook\\n\\nShip it."}'

curl -sX POST https://app.nz/api/artifacts \\
  -H "Authorization: Bearer pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"kind":"sheet","title":"Budget","mime":"application/json","data":"[{\"name\":\"Sheet1\",\"rows\":{\"0\":{\"cells\":{\"0\":{\"text\":\"Item\"},\"1\":{\"text\":\"Cost\"}}}}}]"}'`,
    },
  },
  {
    id: 'media-optimizer',
    label: 'Media optimizer',
    icon: Image,
    title: 'Media optimizer API',
    intro:
      'Cloudflare-style image resizing and AV1-first video re-encoding over your app.nz artifact filesystem. Optimize an existing artifact or a public HTTPS URL, save generated variants back into /artifacts, and serve best-size images through a dynamic URL that negotiates AVIF, WebP, JPEG, or PNG from the Accept header. The same optimizer is available as a Cog image for isolated GPU worker pools.',
    baseUrl: 'https://app.nz',
    endpoints: [
      { method: 'GET', path: '/api/media/optimizer/config', summary: 'Optimizer presets, encoder availability, and public pricing metadata', auth: 'none' },
      { method: 'POST', path: '/api/media/optimizer/estimate', summary: 'Estimate variants and credits without running an encode', auth: 'none' },
      { method: 'POST', path: '/api/media/optimize', summary: 'Generate image/video variants and save them into your artifact filesystem', auth: 'key' },
      { method: 'POST', path: '/v1/media/optimizations', summary: 'OpenAI-style alias for POST /api/media/optimize', auth: 'key' },
      { method: 'GET', path: '/api/media/image/{artifact_id}', summary: 'Dynamic best-size image transform: ?w=640&format=auto&q=85', auth: 'key' },
    ],
    params: {
      heading: 'Optimization body',
      rows: [
        { name: 'artifactId', type: 'string', desc: 'Source artifact to read. Required unless url is supplied.' },
        { name: 'url', type: 'string', desc: 'Public HTTPS source URL. Private/reserved network targets are rejected.' },
        { name: 'kind', type: 'string', desc: 'image | video. Inferred from MIME/extension when omitted.' },
        { name: 'widths', type: 'int[]', desc: 'Image widths to generate. Default: [320,640,960,1280].' },
        { name: 'heights', type: 'int[]', desc: 'Video ladder heights. Default: [360,720,1080].' },
        { name: 'formats', type: 'string[]', desc: 'Image: webp | avif | jpeg | png. Video: webm | av1 | mp4.' },
        { name: 'quality', type: 'int', desc: '1-100 quality target; defaults to 85 for images.' },
        { name: 'outputPath', type: 'string', desc: 'Artifact directory for generated variants; defaults to /optimized.' },
        { name: 'dryRun', type: 'boolean', desc: 'Return the plan and estimated credits without encoding.' },
      ],
    },
    example: {
      label: 'Generate responsive variants and build the Cog image',
      code: `# Save responsive image variants back into /artifacts.
curl -sX POST https://app.nz/api/media/optimize \\
  -H "Authorization: Bearer pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "artifactId": "artifact_123",
    "kind": "image",
    "widths": [320, 640, 1280],
    "formats": ["webp"],
    "outputPath": "/optimized"
  }'

# Dynamic best-size image URL.
curl -L "https://app.nz/api/media/image/artifact_123?w=640&format=auto&q=85" \\
  -H "Authorization: Bearer pk_live_..."

# CLI equivalents.
app media optimize --artifact-id artifact_123 --width 640 --width 1280 --format webp
app media pricing
app builds media-optimizer-cog`,
    },
  },
  {
    id: 'assistants',
    label: 'Character chat',
    icon: Bot,
    title: 'Character chat API',
    intro:
      'Start chats with any character by url_name or id, pin a model for that chat, and stream replies over the same server-sent-events shape as the OpenAI-compatible gateway. User-created characters can include long-form text or Markdown documents; app.nz strips image links, chunks the text, stores it in the character vector-search boundary, and retrieves relevant excerpts for each turn.',
    baseUrl: 'https://app.nz',
    endpoints: [
      { method: 'GET', path: '/api/search-ais?q=luna&limit=20', summary: 'Find characters by name, title, tags, or description', auth: 'none' },
      { method: 'GET', path: '/api/get-ai-by-name?name=luna', summary: 'Load one character by url_name or display name', auth: 'none' },
      { method: 'POST', path: '/api/characters', summary: 'Create a user-owned character and ingest text/Markdown docs', auth: 'key' },
      { method: 'PATCH', path: '/api/characters/{url_name}', summary: 'Update your character and optionally replace indexed docs', auth: 'key' },
      { method: 'POST', path: '/api/assistant/chats', summary: 'Create a chat for a character with a chosen model', auth: 'key' },
      { method: 'POST', path: '/api/assistant/chats/{id}/messages', summary: 'Send a turn and stream the character reply', auth: 'key' },
      { method: 'GET', path: '/api/assistant/chats/{id}', summary: 'Reload a chat and its active branch', auth: 'key' },
    ],
    params: {
      heading: 'Character and chat bodies',
      rows: [
        { name: 'name', type: 'string', required: true, desc: 'POST /api/characters display name.' },
        { name: 'system_prompt', type: 'string', desc: 'Optional persona prompt; generated from name/description/greeting if omitted.' },
        { name: 'documents', type: 'array', desc: 'Optional long-form docs: [{ "name": "lore.md", "content": "# Markdown..." }]. Alias: knowledge_docs.' },
        { name: 'character_url_name', type: 'string', required: true, desc: 'POST /api/assistant/chats target character slug.' },
        { name: 'model', type: 'string', desc: 'Model or route for this chat, e.g. app/auto or app/auto-fast.' },
        { name: 'reasoning_effort', type: 'string', desc: 'auto | off | low | medium | high | xhigh.' },
        { name: 'content', type: 'string', required: true, desc: 'Message text for POST /messages.' },
      ],
    },
    example: {
      label: 'Create a documented character and talk to it with a model',
      code: `# Create a character with long-form Markdown knowledge.
curl -sX POST https://app.nz/api/characters \\
  -H "Authorization: Bearer pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "GPU Brain Operator",
    "description": "Helps operate the gpu-brain search stack.",
    "voice": "Kore",
    "documents": [{
      "name": "runbook.md",
      "content": "# Runbook\\nUse gobed for low-latency KNN over text chunks. Never ingest image links."
    }]
  }'

# Start a chat with a given model.
CHAT=$(curl -sX POST https://app.nz/api/assistant/chats \\
  -H "Authorization: Bearer pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "character_url_name": "gpu-brain-operator",
    "model": "app/auto-fast",
    "reasoning_effort": "auto"
  }' | jq -r .chat.id)

# Send a message. The response is text/event-stream; relevant doc excerpts are
# retrieved server-side before the model call.
curl -N -sX POST https://app.nz/api/assistant/chats/$CHAT/messages \\
  -H "Authorization: Bearer pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"content":"What should I check if KNN search looks stale?"}'`,
    },
  },
  {
    id: 'repos',
    label: 'Repos',
    icon: GitBranch,
    title: 'Repos API — git & GitHub parity',
    intro:
      'GitHub-parity repo hosting on real bare git repositories: clone and push over standard git smart HTTP (git clone https://app.nz/{owner}/{name}.git), plus issues, pull requests with inline line-anchored review comments, a git-backed wiki, releases, labels, Actions-style secrets/variables, and branch-protection rulesets. Read endpoints (repo detail, git browsing, issues/pulls/releases/labels/rulesets, wiki) work with no key at all for public repos — the same code path renders the showcase browser for signed-out visitors — and need a key with read access (owner or a team member) for private ones. Everything that mutates state needs a key with write access, except fork and star, which only need any signed-in reader. Mentioning @agent (or @codex, @claude, @bot, /agent, /fix, /review) in a pull-request comment launches a coding agent on that PR\'s branch and posts a bot reply linking the task.',
    baseUrl: 'https://app.nz',
    endpoints: [
      // Repo CRUD + discovery
      { method: 'GET', path: '/api/repos', summary: 'List your repos', auth: 'key' },
      { method: 'POST', path: '/api/repos', summary: 'Create a repo (name, description, visibility, org)', auth: 'key' },
      { method: 'GET', path: '/api/repos/explore', summary: 'Public repos for the showcase grid (?kind=code|dataset|model&sort=stars|recent)', auth: 'none' },
      { method: 'GET', path: '/api/repos/search', summary: 'Search public repos by name/description (?q=)', auth: 'none' },
      { method: 'GET', path: '/api/repos/lookup/{owner}/{name}', summary: 'Resolve owner/name to a repo (for pretty URLs)', auth: 'none' },
      { method: 'GET', path: '/api/repos/{id}', summary: 'Repo detail — issues, pulls, branches, recent commits', auth: 'none' },
      { method: 'PATCH', path: '/api/repos/{id}', summary: 'Edit name, description, visibility, defaultBranch, imageUrl', auth: 'key' },
      { method: 'DELETE', path: '/api/repos/{id}', summary: 'Archive (soft-delete) a repo', auth: 'key' },
      { method: 'POST', path: '/api/repos/{id}/fork', summary: 'Fork into your namespace (real bare-git clone, full history)', auth: 'key' },
      { method: 'GET', path: '/api/repos/{id}/star', summary: 'Viewer’s star state and total count', auth: 'none' },
      { method: 'PUT', path: '/api/repos/{id}/star', summary: 'Star the repo', auth: 'key' },
      { method: 'DELETE', path: '/api/repos/{id}/star', summary: 'Unstar the repo', auth: 'key' },
      // Git browsing (read-only, backed by the real bare repo on disk)
      { method: 'GET', path: '/api/repos/{id}/git/branches', summary: 'List branches', auth: 'none' },
      { method: 'GET', path: '/api/repos/{id}/git/tree', summary: 'Directory listing (?ref=&path=&withCommits=1)', auth: 'none' },
      { method: 'GET', path: '/api/repos/{id}/git/paths', summary: 'Flat file-path list for a fuzzy finder (?ref=)', auth: 'none' },
      { method: 'GET', path: '/api/repos/{id}/git/blob', summary: 'File content, size, language, latest commit (?ref=&path=)', auth: 'none' },
      { method: 'GET', path: '/api/repos/{id}/git/raw', summary: 'Raw file bytes with a sniffed content-type (?ref=&path=)', auth: 'none' },
      { method: 'GET', path: '/api/repos/{id}/git/log', summary: 'Commit history (?ref=&path=&limit=)', auth: 'none' },
      { method: 'GET', path: '/api/repos/{id}/git/commit/{sha}', summary: 'Commit metadata plus its file diff', auth: 'none' },
      { method: 'GET', path: '/api/repos/{id}/git/diff', summary: 'Diff and commit range between two refs (?base=&head=)', auth: 'none' },
      { method: 'GET', path: '/api/repos/{id}/git/readme', summary: 'Rendered README lookup for a ref/path', auth: 'none' },
      { method: 'GET', path: '/api/repos/{id}/git/search', summary: 'Code search — semantic by default, ?mode=keyword for git grep (?q=&limit=)', auth: 'none' },
      // Branches & commits bookkeeping (real code changes are pushed over git, not these)
      { method: 'POST', path: '/api/repos/{id}/branches', summary: 'Create a branch (name, from)', auth: 'key' },
      { method: 'POST', path: '/api/repos/{id}/commits', summary: 'Record a commit entry (branch, message) — bookkeeping only, not a real git commit', auth: 'key' },
      // Issues
      { method: 'GET', path: '/api/repos/{id}/issues', summary: 'List issues (?state=open|closed|all)', auth: 'none' },
      { method: 'GET', path: '/api/repos/{id}/issues/{number}', summary: 'Issue detail with its comment thread', auth: 'none' },
      { method: 'POST', path: '/api/repos/{id}/issues', summary: 'Open an issue (title, body)', auth: 'key' },
      { method: 'PATCH', path: '/api/repos/{id}/issues/{number}', summary: 'Set state (state: open|closed)', auth: 'key' },
      { method: 'POST', path: '/api/repos/{id}/issues/{number}/comments', summary: 'Comment on an issue (body)', auth: 'key' },
      // Pull requests
      { method: 'GET', path: '/api/repos/{id}/pulls', summary: 'List pull requests (?state=open|closed|all)', auth: 'none' },
      { method: 'GET', path: '/api/repos/{id}/pulls/{number}', summary: 'Pull request detail with its comment thread', auth: 'none' },
      { method: 'POST', path: '/api/repos/{id}/pulls', summary: 'Open a PR (title, body, sourceBranch, targetBranch)', auth: 'key' },
      { method: 'PATCH', path: '/api/repos/{id}/pulls/{number}', summary: 'Set state (state: open|closed|merged — merged performs a real git fast-forward or merge commit)', auth: 'key' },
      { method: 'GET', path: '/api/repos/{id}/pulls/{number}/comments', summary: 'List PR comments, including line-anchored review comments', auth: 'none' },
      { method: 'POST', path: '/api/repos/{id}/pulls/{number}/comments', summary: 'Post a PR comment, optionally anchored to a diff line (body, filePath, lineStart, lineEnd, side)', auth: 'key' },
      // Wiki (git-backed: every save is a real commit to a sibling *.wiki.git repo)
      { method: 'GET', path: '/api/repos/{id}/wiki', summary: 'List wiki pages', auth: 'none' },
      { method: 'GET', path: '/api/repos/{id}/wiki/pages/{slug}', summary: 'Get a wiki page', auth: 'none' },
      { method: 'GET', path: '/api/repos/{id}/wiki/pages/{slug}/history', summary: 'Page history from the wiki git repo', auth: 'none' },
      { method: 'GET', path: '/api/repos/{id}/wiki/search', summary: 'Search wiki pages (?q=)', auth: 'none' },
      { method: 'PUT', path: '/api/repos/{id}/wiki/pages/{slug}', summary: 'Create or update a page (title, body, message)', auth: 'key' },
      { method: 'DELETE', path: '/api/repos/{id}/wiki/pages/{slug}', summary: 'Delete a page', auth: 'key' },
      // Releases
      { method: 'GET', path: '/api/repos/{id}/releases', summary: 'List releases', auth: 'none' },
      { method: 'GET', path: '/api/repos/{id}/releases/{tag}', summary: 'Get one release', auth: 'none' },
      { method: 'POST', path: '/api/repos/{id}/releases', summary: 'Create a release (tag, name, body, target, prerelease, draft)', auth: 'key' },
      { method: 'PATCH', path: '/api/repos/{id}/releases/{tag}', summary: 'Update a release', auth: 'key' },
      { method: 'DELETE', path: '/api/repos/{id}/releases/{tag}', summary: 'Delete a release', auth: 'key' },
      // Labels, secrets, variables, rulesets (CI/collaboration parity)
      { method: 'GET', path: '/api/repos/{id}/labels', summary: 'List labels', auth: 'none' },
      { method: 'POST', path: '/api/repos/{id}/labels', summary: 'Create a label (name, color, description)', auth: 'key' },
      { method: 'DELETE', path: '/api/repos/{id}/labels/{name}', summary: 'Delete a label', auth: 'key' },
      { method: 'GET', path: '/api/repos/{id}/secrets', summary: 'List secret names — values are write-only and never returned', auth: 'key' },
      { method: 'POST', path: '/api/repos/{id}/secrets', summary: 'Set a secret (name, value)', auth: 'key' },
      { method: 'DELETE', path: '/api/repos/{id}/secrets/{name}', summary: 'Delete a secret', auth: 'key' },
      { method: 'GET', path: '/api/repos/{id}/variables', summary: 'List variables (values are readable)', auth: 'key' },
      { method: 'POST', path: '/api/repos/{id}/variables', summary: 'Set a variable (name, value)', auth: 'key' },
      { method: 'DELETE', path: '/api/repos/{id}/variables/{name}', summary: 'Delete a variable', auth: 'key' },
      { method: 'GET', path: '/api/repos/{id}/rulesets', summary: 'List branch-protection rulesets', auth: 'none' },
      { method: 'POST', path: '/api/repos/{id}/rulesets', summary: 'Create a ruleset (name, targetBranch, requirePr, requireChecks, blockForcePush)', auth: 'key' },
      { method: 'DELETE', path: '/api/repos/{id}/rulesets/{name}', summary: 'Delete a ruleset', auth: 'key' },
      // Real git, over smart HTTP — not JSON, but the canonical way to move code
      { method: 'GET', path: 'https://app.nz/{owner}/{name}.git/info/refs', summary: 'git clone/pull ref advertisement (git clone https://app.nz/{owner}/{name}.git)', auth: 'none', noDoc: true },
      { method: 'POST', path: 'https://app.nz/{owner}/{name}.git/git-receive-pack', summary: 'git push — HTTP Basic auth, an API key as the password', auth: 'key', noDoc: true },
    ],
    params: {
      heading: 'Access levels',
      rows: [
        { name: 'read', type: 'level', desc: 'Anyone, for public repos; owner + team viewers for private ones. Covers repo detail, git browsing, issues/pulls/releases/labels/rulesets, and wiki reads.' },
        { name: 'write', type: 'level', desc: 'Owner, global admins, or a team member/admin on the repo’s governing org. Required for branches, commits, issues, pulls, wiki writes, labels, secrets, variables, rulesets, releases.' },
        { name: 'fork / star', type: 'level', desc: 'Any signed-in reader — no write access required.' },
        { name: 'admin', type: 'level', desc: 'Owner or global admin. Required to edit or delete the repo itself.' },
      ],
    },
    example: {
      label: 'Create a repo, push real git, open a PR, ask an agent to fix something',
      code: `# Create the repo (private by default)
curl -sX POST https://app.nz/api/repos \\
  -H "Authorization: Bearer pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"name":"my-app","description":"demo","visibility":"public"}'
# -> {"repo":{"id":"repo_...","fullName":"you/my-app",...}}

# Real git — clone/push over smart HTTP, an API key as the Basic-auth password
git clone https://app.nz/you/my-app.git
cd my-app && git checkout -b feature/x && git commit --allow-empty -m x
git push https://token:pk_live_...@app.nz/you/my-app.git feature/x

# Open a PR against main
curl -sX POST https://app.nz/api/repos/repo_.../pulls \\
  -H "Authorization: Bearer pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"title":"Add feature x","sourceBranch":"feature/x","targetBranch":"main"}'

# @mention an agent on the PR thread — it launches on the PR branch and replies
curl -sX POST https://app.nz/api/repos/repo_.../pulls/1/comments \\
  -H "Authorization: Bearer pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"body":"@agent add input validation and a test for the empty case"}'`,
    },
  },
  {
    id: 'sites',
    label: 'Sites',
    icon: Globe,
    title: 'Sites API',
    intro:
      'Host a static site straight from the control plane. Create a slug, push files (index.html + assets, text or binary), and it is published instantly at both app.nz/sites/<slug>/ and its own subdomain <slug>.app.nz. No build step, no DNS wait. Create with AI or upload a ZIP in the web UI. Static publishing needs no funds. The subdomain is the right home for SPAs — bundlers emit absolute /assets/… paths that only resolve from a site root — and it is served straight from Cloudflare R2 at the edge, so static traffic never touches an origin. Platform session cookies are host-only: add user accounts safely by registering the exact https://<slug>.app.nz/auth/callback URL at /developers and using appnz-auth.js with PKCE. A fresh site is seeded with a working starter (index.html, styles.css, app.js). Fastest path from a built folder is one command: `app sites deploy my-site ./dist` — it creates the site on first run, uploads everything, and prunes what you deleted. Slugs are 1–40 chars: lowercase letters, numbers, and hyphens (single letters like h.app.nz are fine), and a few reserved names are blocked. Files are up to 64 MB each; binary assets (images, fonts, audio, wasm) are stored too. Visiting a name nobody has published returns a 404 that names the free slug and lists the exact create commands — and answers JSON to `Accept: application/json` so agents can read it.',
    baseUrl: 'https://app.nz',
    endpoints: [
      { method: 'GET', path: '/api/sites', summary: 'List your sites', auth: 'key' },
      { method: 'POST', path: '/api/sites', summary: 'Create a site (seeds a starter)', auth: 'key' },
      { method: 'GET', path: '/api/sites/{id}', summary: 'Get a site with its files', auth: 'key' },
      { method: 'DELETE', path: '/api/sites/{id}', summary: 'Delete a site', auth: 'key' },
      { method: 'PUT', path: '/api/sites/{id}/files', summary: 'Create or update a file (publishes)', auth: 'key' },
      { method: 'DELETE', path: '/api/sites/{id}/files?path=app.js', summary: 'Delete a file', auth: 'key' },
      { method: 'GET', path: '/sites/{slug}/{path}', summary: 'Public serving — no auth, defaults to index.html', auth: 'none', noDoc: true },
      { method: 'GET', path: 'https://{slug}.app.nz/{path}', summary: 'Public serving on the site’s own subdomain (SPA-friendly)', auth: 'none', noDoc: true },
    ],
    params: {
      heading: 'Request bodies',
      rows: [
        { name: 'slug', type: 'string', required: true, desc: 'POST /api/sites — public name; normalized to a valid slug.' },
        { name: 'title', type: 'string', desc: 'POST /api/sites — display title (max 120 chars).' },
        { name: 'path', type: 'string', required: true, desc: 'PUT files — file path, e.g. index.html or css/app.css.' },
        { name: 'content', type: 'string', desc: 'PUT files — UTF-8 file contents (max 8 MB). Use for text files.' },
        { name: 'contentBase64', type: 'string', desc: 'PUT files — base64 bytes for binary files (images, fonts, audio).' },
        { name: 'contentType', type: 'string', desc: 'PUT files — overrides the type inferred from the extension.' },
      ],
    },
    example: {
      label: 'Deploy a built site with the CLI (or raw curl)',
      code: `# Easiest: push a whole build directory with the app CLI. It creates the
# site on first run, uploads every text file, and prunes anything removed.
app login
bun run build                         # e.g. Vite -> dist/
app sites deploy my-app dist --title "My app"
#   Live at https://app.nz/sites/my-app/
#     and  https://my-app.app.nz/

# Or drive the REST API directly:
SITE=$(curl -sX POST https://app.nz/api/sites \\
  -H "Authorization: Bearer pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"slug":"my-landing","title":"My landing"}')

ID=$(echo "$SITE" | jq -r .site.id)

# Publish index.html — live at /sites/my-landing/ and my-landing.app.nz
curl -sX PUT https://app.nz/api/sites/$ID/files \\
  -H "Authorization: Bearer pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"path":"index.html","content":"<!doctype html><h1>Hi</h1>"}'`,
    },
  },
  {
    id: 'cogs',
    label: 'Cogs',
    icon: Cpu,
    title: 'Cogs API — GPU model hosting',
    intro:
      'Run an arbitrary Cog container as an autoscaling, scale-to-zero inference endpoint. Register any compliant image once, or use POST /api/cogs/run to atomically ensure a vetted template and queue work in one call. Low traffic can use serverless workers; sustained demand promotes to a warm pod through the GPU router shared with ComfyUI. The idle reaper checks active predictions before terminating the worker, and every response exposes an immediate sleep path. Comfy graphs use template:name references, so durable workflows never contain pod ids or endpoints. Media travels as HTTPS URLs or data URIs, making Pocket TTS, Fish Speech, Audex speech-to-speech, image, video, and 3D Cogs first-class.',
    baseUrl: 'https://app.nz',
    endpoints: [
      { method: 'GET', path: '/api/cogs/config', summary: 'GPU hardware catalogue, starter templates, output kinds, markup', auth: 'none' },
      { method: 'GET', path: '/api/cogs', summary: 'List your registered models', auth: 'key' },
      { method: 'POST', path: '/api/cogs', summary: 'Register a model (name, image, hardware, schema, idleSeconds, minVramGb)', auth: 'key' },
      { method: 'POST', path: '/api/cogs/run', summary: 'Ensure a vetted template or use modelId, then queue inference with scale-to-zero lifecycle metadata', auth: 'key' },
      { method: 'POST', path: '/api/cogs/spaces/import', summary: 'Discover a Gradio Hugging Face Space and deploy its published image with generated API/UI and scale-to-zero', auth: 'key' },
      { method: 'GET', path: '/api/cogs/{id}', summary: 'Model detail plus its 20 most recent predictions', auth: 'key' },
      { method: 'DELETE', path: '/api/cogs/{id}', summary: 'Delete a model (tears down any warm pod first)', auth: 'key' },
      { method: 'POST', path: '/api/cogs/{id}/warm', summary: 'Proactively cold-start the pod', auth: 'key' },
      { method: 'POST', path: '/api/cogs/{id}/sleep', summary: 'Scale the model to zero now', auth: 'key' },
      { method: 'POST', path: '/api/cogs/{id}/predict', summary: 'Run a prediction asynchronously (input) — poll for the result', auth: 'key' },
      { method: 'GET', path: '/api/cogs/{id}/predictions', summary: 'List a model’s recent predictions (up to 50)', auth: 'key' },
      { method: 'GET', path: '/api/cogs/predictions/{id}', summary: 'Poll a prediction by id — status, output, cost, timing', auth: 'key' },
    ],
    params: {
      heading: 'POST /api/cogs body',
      rows: [
        { name: 'name', type: 'string', required: true, desc: 'Model display name.' },
        { name: 'image', type: 'string', required: true, desc: 'Container image, e.g. r8.im/owner/model or your own registry image.' },
        { name: 'hardware', type: 'string', desc: 'A GPU machine id (gpu-t4, gpu-rtx3090, gpu-rtx4090, gpu-a40, gpu-l40s, gpu-a100, gpu-h100), or "auto"/omitted to pick the cheapest GPU that fits minVramGb.' },
        { name: 'schema', type: 'object', desc: 'Optional { inputs: [{name,type,description,default,required,choices,min,max,order}], outputKind }. Omitted schemas are introspected from the container’s /openapi.json on first warm-up.' },
        { name: 'idleSeconds', type: 'integer', desc: 'Idle window before scale-to-zero (default 120).' },
        { name: 'minVramGb', type: 'integer', desc: 'Model’s VRAM floor, used for auto hardware selection.' },
      ],
    },
    example: {
      label: 'Ensure open audio or register any Cog',
      code: `# One call: ensure Pocket TTS, queue it, and reuse it on later calls
curl -sX POST https://app.nz/api/cogs/run \\
  -H "Authorization: Bearer $APP_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"template":"pocket-tts","input":{"text":"Kia ora","voice":"alba","format":"wav"}}'
# -> {"created":true,"model":{"id":"..."},"prediction":{"id":"..."},
#     "lifecycle":{"scaleToZero":true,"idleSeconds":60,"status":"...","sleep":"..."}}

# Any Cog image can still be registered directly
curl -sX POST https://app.nz/api/cogs \\
  -H "Authorization: Bearer pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"name":"fast-vfx","image":"r8.im/lee101/fast-vfx","hardware":"gpu-rtx4090"}'
# -> {"model":{"id":"cog_...","status":"idle",...}}

curl -sX POST https://app.nz/api/cogs/cog_.../predict \\
  -H "Authorization: Bearer pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"input":{"video":"https://example.com/clip.mp4","num_levels":25}}'
# -> {"prediction":{"id":"pred_...","status":"starting"}}

# Poll for the result (a cold start can take a couple of minutes)
curl -s https://app.nz/api/cogs/predictions/pred_... \\
  -H "Authorization: Bearer pk_live_..."

# Audio-to-audio (audex-s2s template): one spoken turn per prediction
curl -sX POST https://app.nz/api/cogs/cog_.../predict \\
  -H "Authorization: Bearer pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"input":{"task":"converse","audio":"data:audio/webm;base64,...","system_prompt":"You are a cheerful pirate captain."}}'
# -> output: {"transcript":"...","response_text":"...","audio":"data:audio/mpeg;base64,..."}`,
    },
  },
  {
    id: 'notebooks',
    label: 'Notebooks & datasets',
    icon: NotebookPen,
    title: 'Notebooks & datasets',
    intro:
      'Hosted marimo notebooks run free in the browser (Pyodide) or on per-minute cloud machines that stop when idle. Datasets accept csv, json, jsonl (agent traces auto-detected), parquet, sqlite .db files, images, and browser-playable video; binary formats stream from the CDN and table previews open in the Sheets editor. Machine rates include the platform margin — what /api/notebooks/pricing returns is what you pay.',
    baseUrl: 'https://app.nz',
    endpoints: [
      { method: 'GET', path: '/api/notebooks', summary: 'List your notebooks plus public examples', auth: 'key' },
      { method: 'POST', path: '/api/notebooks', summary: 'Create a notebook ({name, source?, forkOf?})', auth: 'key' },
      { method: 'GET', path: '/api/notebooks/{slug}', summary: 'Fetch a notebook with its marimo source', auth: 'key' },
      { method: 'PUT', path: '/api/notebooks/{slug}', summary: 'Update name, source, visibility, runtime', auth: 'key' },
      { method: 'DELETE', path: '/api/notebooks/{slug}', summary: 'Delete a notebook (stops its sessions)', auth: 'key' },
      { method: 'GET', path: '/api/notebooks/{slug}/sessions', summary: 'List cloud sessions with status and endpoint', auth: 'key' },
      { method: 'POST', path: '/api/notebooks/{slug}/sessions', summary: 'Start a cloud session ({machineType}), billed per minute', auth: 'key' },
      { method: 'DELETE', path: '/api/notebooks/sessions/{id}', summary: 'Stop a session and settle billing', auth: 'key' },
      { method: 'GET', path: '/api/notebooks/pricing', summary: 'Machine catalogue with hourly/credit rates and storage pricing', auth: 'none' },
      { method: 'POST', path: '/api/datasets/upload', summary: 'Upload a table, database, image, or video dataset', auth: 'key' },
      { method: 'GET', path: '/api/datasets/u/{id}/notebook', summary: 'Generated marimo starter notebook for a dataset', auth: 'key' },
    ],
    example: {
      label: 'Create a notebook and run it on a GPU',
      code: `curl -sX POST https://app.nz/api/notebooks \\
  -H "Authorization: Bearer pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"name":"fraud analysis"}'

curl -sX POST https://app.nz/api/notebooks/nb-1a2b3c4d/sessions \\
  -H "Authorization: Bearer pk_live_..." \\
  -d '{"machineType":"gpu-rtx3090"}'
# -> {"session":{"id":"...","status":"provisioning","priceHourly":"$0.264"}}`,
    },
  },
  {
    id: 'billing',
    label: 'Billing & plans',
    icon: KeyRound,
    title: 'Billing & plans',
    intro:
      'Everything is paid from one prepaid credit balance (credits are ~$0.001 each). On top of pay-as-you-go, a Pro ($20), Ultra ($60), or Max ($200) plan grants monthly credits, a guaranteed number of concurrent machines, and per-product free quotas. Plan credits are used before purchased credits and roll over for one month.',
    baseUrl: 'https://app.nz',
    endpoints: [
      { method: 'GET', path: '/api/pricing', summary: 'Usage-based pricing: model token rates, compute, search, credit unit', auth: 'none' },
      { method: 'GET', path: '/api/plans', summary: 'List plan catalog (price, credits, machines, quotas)', auth: 'none' },
      { method: 'GET', path: '/api/plans/me', summary: 'Your plan, credit balance, and product quota usage', auth: 'key' },
      { method: 'POST', path: '/api/plans/subscribe', summary: 'Start a Pro/Ultra/Max checkout', auth: 'session' },
      { method: 'POST', path: '/api/plans/cancel', summary: 'Cancel at period end', auth: 'session' },
      { method: 'POST', path: '/api/plans/portal', summary: 'Open the Stripe billing portal', auth: 'session' },
      { method: 'GET', path: '/api/credits/balance', summary: 'Prepaid credit balance + auto-topup', auth: 'key' },
      { method: 'POST', path: '/api/credits/checkout', summary: 'Buy credit packs', auth: 'session' },
      { method: 'POST', path: '/api/credits/autotopup', summary: 'Configure automatic top-ups', auth: 'session' },
    ],
    params: {
      heading: 'Plan tiers',
      rows: [
        { name: 'free', type: 'plan', desc: '$0 — pay-as-you-go credits, shared worker pool.' },
        { name: 'pro', type: 'plan', desc: '$20/mo — 25,000 credits, 1 guaranteed machine.' },
        { name: 'ultra', type: 'plan', desc: '$60/mo — 80,000 credits, 3 machines + priority.' },
        { name: 'max', type: 'plan', desc: '$200/mo — 280,000 credits, 6 machines + top priority.' },
      ],
    },
    example: {
      label: 'Check your plan and subscribe from the CLI',
      code: `# Inspect the catalog
curl -s https://app.nz/api/plans | jq

# From the CLI (app login first)
app plan show
app plan subscribe ultra
app billing usage`,
    },
  },
  {
    id: 'payments',
    label: 'Payments (earn)',
    icon: KeyRound,
    title: 'Payments — let your app earn',
    intro:
      'Accept payments in anything you build on app.nz. Under the hood it is Stripe Connect with direct charges: you are the merchant of record on your own connected account, Stripe handles KYC/verification and risk, and app.nz white-labels the whole experience. You pay one flat retail rate per plan (Free 5% + 30¢, Pro 4%, Ultra 3.5%, Max 3% — see /api/payments/config), collected automatically as an application fee on each charge. Standard payouts are free; instant payouts cost 1.5% (min 50¢). Earnings can be converted into app.nz credits so an app that earns pays for its own hosting.',
    baseUrl: 'https://app.nz',
    endpoints: [
      { method: 'GET', path: '/api/payments/config', summary: 'Fee schedule per plan, limits, instant-payout pricing', auth: 'none' },
      { method: 'POST', path: '/api/payments/enable', summary: 'Create your payment account; returns a hosted onboarding link', auth: 'key' },
      { method: 'GET', path: '/api/payments/account', summary: 'Onboarding status: chargesEnabled, payoutsEnabled, your fee rate', auth: 'key' },
      { method: 'POST', path: '/api/payments/checkout', summary: 'Sell: one-time or recurring checkout; returns a Stripe-hosted payment url', auth: 'key' },
      { method: 'GET', path: '/api/payments/sales', summary: 'List checkouts with paid totals and fees', auth: 'key' },
      { method: 'GET', path: '/api/payments/balance', summary: 'Available / pending / instant-eligible balance', auth: 'key' },
      { method: 'POST', path: '/api/payments/payout', summary: 'Pay out to your bank; {"instant": true} arrives in ~30 min for a fee', auth: 'key' },
      { method: 'POST', path: '/api/payments/offset', summary: 'Convert earnings into app.nz credits (1¢ = 10 credits) to pay hosting', auth: 'key' },
      { method: 'POST', path: '/api/payments/settings', summary: 'Toggle automatic hosting offset', auth: 'key' },
      { method: 'POST', path: '/api/payments/webhook', summary: 'Stripe Connect webhook receiver (platform-facing)', auth: 'none', noDoc: true },
    ],
    params: {
      heading: 'Checkout body',
      rows: [
        { name: 'amount', type: 'int', required: true, desc: 'Unit price in cents (50 – 5,000,000).' },
        { name: 'description', type: 'string', desc: 'What the customer is buying; shows on the checkout page.' },
        { name: 'recurring', type: 'string', desc: 'Omit for one-time; "month"/"year"/"week"/"day" for a subscription.' },
        { name: 'currency', type: 'string', desc: 'ISO currency, default usd.' },
        { name: 'quantity', type: 'int', desc: 'Line-item quantity, default 1.' },
        { name: 'successUrl', type: 'string', desc: 'Where the buyer lands after paying (your app).' },
        { name: 'cancelUrl', type: 'string', desc: 'Where the buyer lands if they abandon checkout.' },
        { name: 'metadata', type: 'object', desc: 'Your own keys (order id, plan) echoed back on the sale.' },
      ],
    },
    example: {
      label: 'Enable once, then sell a $29/mo plan from your backend',
      code: `# One-time setup: onboard (opens Stripe-hosted KYC, bank account)
app payments enable --country NZ --description "Pro subscriptions"

# From your app's server (never expose the key in browser code):
curl -sX POST https://app.nz/api/payments/checkout \\
  -H "Authorization: Bearer $APP_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"amount": 2900, "description": "Pro plan", "recurring": "month",
       "successUrl": "https://myapp.app.nz/thanks"}'
# → redirect the buyer to the returned "url"

app payments sales            # who paid
app payments balance          # available / pending / instant
app payments payout --amount 100.00 --instant   # ~30 min, 1.5% fee
app payments offset --amount 50.00              # $50 earnings → 50,000 hosting credits`,
    },
  },
  {
    id: 'spatial-assets',
    label: 'Animation & 3D',
    icon: Box,
    title: 'Open animation and spatial asset API',
    intro:
      'Search the public motion, VFX, terrain, procedural-object, and image-to-3D index by stable ID. Agents can inspect source and license metadata, download portable JSON recipes, fetch OBJ meshes, build deterministic terrain, or prepare an image reconstruction job without parsing the web gallery.',
    baseUrl: 'https://app.nz',
    endpoints: [
      { method: 'GET', path: '/api/animation-library', summary: 'Search the indexed asset manifest with q, kind, and limit', auth: 'none' },
      { method: 'GET', path: '/api/animation-library/index.json', summary: 'Fetch the complete cacheable index for offline agent search', auth: 'none' },
      { method: 'GET', path: '/api/animation-library/{id}/download?format=json', summary: 'Download a recipe; use format=obj for terrain and 3D objects', auth: 'none' },
      { method: 'POST', path: '/api/animation-library/terrain', summary: 'Build a deterministic terrain recipe', auth: 'none' },
      { method: 'POST', path: '/api/animation-library/image-to-3d', summary: 'Build an image-to-3D reconstruction recipe', auth: 'none' },
      { method: 'POST', path: '/v1/3d/generations', summary: 'Run an authenticated 3D generation job from image or text input', auth: 'key' },
    ],
    params: {
      heading: 'Search and download parameters',
      rows: [
        { name: 'q', type: 'string', desc: 'Substring matched against stable id, name, description, and tags.' },
        { name: 'kind', type: 'enum', desc: 'pose, motion, vfx, terrain, object, or image3d.' },
        { name: 'format', type: 'enum', desc: 'json for every asset; obj for terrain, object, and image3d.' },
        { name: 'target_polycount', type: 'integer', desc: 'Requested reconstruction budget, clamped to 500,000.' },
      ],
    },
    example: {
      label: 'Agent search → inspect → download',
      code: `# Find downloadable image-to-3D assets
curl -s 'https://app.nz/api/animation-library?q=product&kind=image3d&limit=10' | jq

# Download portable geometry
curl -L 'https://app.nz/api/animation-library/image3d-sneaker/download?format=obj' -o sneaker.obj

# Equivalent CLI flow
app animation search product --kind image3d
app animation download image3d-sneaker --format obj --output sneaker.obj

# MCP tools: search_animation_library, download_animation_asset,
# create_image_to_3d_recipe, generate_terrain_recipe`,
    },
  },
  {
    id: 'products',
    label: 'Products',
    icon: Bot,
    title: 'First-party products',
    intro:
      'RA1 art generation, the Papers API, and agentic search are first-party APIs metered against the same balance. Plan subscribers get a monthly free quota per product, consumed before any credits are charged. Interactive Papers search at /papers is public and free; authenticated API, CLI, desktop, and MCP calls keep the shared usage contract.',
    baseUrl: 'https://app.nz',
    endpoints: [
      { method: 'POST', path: '/api/ra1/generate', summary: 'Generate images with RA1 — prompt, count, aspect, style, negative (50 credits each)', auth: 'key' },
      { method: 'GET', path: '/api/ra1/status', summary: 'Poll a queued render by jobId for status + image URLs', auth: 'key' },
      { method: 'GET', path: '/api/papers/public-search', summary: 'Free browser search across papers, methods, datasets, code, and repos (20-result cap)', auth: 'none' },
      { method: 'GET', path: '/api/papers/search', summary: 'Authenticated Papers API search (1 credit each)', auth: 'key' },
      { method: 'POST', path: '/api/search', summary: 'Agentic web search (10 credits per depth)', auth: 'key' },
      { method: 'POST', path: '/api/packing/solve', summary: '3D bin packing — container + items in, placements/utilization/unplaced out (free)', auth: 'none' },
    ],
    example: {
      label: 'Call the products from the CLI',
      code: `app chat "research R2-backed model hosting" --web --deep --papers
app ra1 generate --prompt "a cat astronaut" --count 2
app papers search "diffusion transformers" --limit 5
app search "best vector database 2026" --depth 2`,
    },
  },
];

export const methodColor: Record<Method, string> = {
  GET: 'text-emerald-300 border-emerald-300/30 bg-emerald-300/10',
  POST: 'text-sky-300 border-sky-300/30 bg-sky-300/10',
  PUT: 'text-amber-300 border-amber-300/30 bg-amber-300/10',
  PATCH: 'text-violet-300 border-violet-300/30 bg-violet-300/10',
  DELETE: 'text-rose-300 border-rose-300/30 bg-rose-300/10',
};

export const authLabel: Record<NonNullable<Endpoint['auth']>, string> = {
  key: 'API key',
  session: 'session',
  none: 'public',
};

// --- Per-endpoint deep links (/docs/{slug}) ---------------------------------
// Each callable endpoint gets a stable, crawlable slug so it can have its own
// documentation page and sitemap entry. Slugs are derived from the path:
// gateway /v1/* routes drop the version prefix (audio/speech -> audio-speech),
// control-plane /api/* routes keep an api- prefix to stay collision-free, and
// path params ({id}) collapse to their name. Endpoints repeated across sections
// (the Agents SDK reuses Agents routes) resolve to the first section's page.

export interface DocEndpoint extends Endpoint {
  slug: string;
  sectionId: string;
  sectionTitle: string;
}

function baseSlug(path: string): string {
  let p = path.split('?')[0];
  p = p.replace(/\{([^}]+)\}/g, '$1'); // {id} -> id
  p = p.replace(/^\/v1\//, '/'); // gateway: drop the version prefix
  p = p.replace(/^\/api\//, '/api-'); // control plane: keep api- to disambiguate
  p = p.replace(/^\//, '');
  return p
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}

const endpointKey = (method: string, path: string) => `${method} ${path}`;

const slugByKey = new Map<string, string>();
const usedSlugs = new Set<string>();
const builtEndpoints: DocEndpoint[] = [];

// Section pages live at /docs/{section.id}. Reserve those slugs so a colliding
// endpoint (GET /v1/models → "models") becomes models-get instead.
for (const section of sections) usedSlugs.add(section.id);

for (const section of sections) {
  for (const e of section.endpoints ?? []) {
    if (e.noDoc) continue;
    const key = endpointKey(e.method, e.path);
    if (slugByKey.has(key)) continue; // duplicate route across sections
    let slug = baseSlug(e.path);
    if (usedSlugs.has(slug)) slug = `${slug}-${e.method.toLowerCase()}`;
    let n = 2;
    while (usedSlugs.has(slug)) slug = `${baseSlug(e.path)}-${e.method.toLowerCase()}-${n++}`;
    usedSlugs.add(slug);
    slugByKey.set(key, slug);
    builtEndpoints.push({ ...e, slug, sectionId: section.id, sectionTitle: section.title });
  }
}

export const docEndpoints: DocEndpoint[] = builtEndpoints;

const bySlug = new Map(docEndpoints.map((e) => [e.slug, e]));

export function docEndpointBySlug(slug: string): DocEndpoint | undefined {
  return bySlug.get(slug);
}

// slugForEndpoint returns the /docs page slug for a list row, or undefined for
// noDoc URL-pattern rows that have no dedicated page.
export function slugForEndpoint(method: string, path: string): string | undefined {
  return slugByKey.get(endpointKey(method, path));
}

export function docSectionById(id: string): DocSection | undefined {
  return sections.find((s) => s.id === id);
}

export const docSectionIds: string[] = sections.map((s) => s.id);

// docEndpointSlugs is the canonical slug list mirrored by the sitemap (TS side
// feeds prerenderPaths; server/sitemap.go keeps a matching Go slice).
export const docEndpointSlugs: string[] = docEndpoints.map((e) => e.slug);
